import { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import axios from 'axios'
import LoadingSpinner from '../common/LoadingSpinner'

const LogVisitModal = ({ isOpen, onClose, onSuccess, gym }) => {
  const [loading, setLoading] = useState(false)
  const [visitType, setVisitType] = useState('')
  const [notes, setNotes] = useState('')
  const [visitDate, setVisitDate] = useState('')
  const [currentAttempt, setCurrentAttempt] = useState(1)
  const [step, setStep] = useState(1) // Step 1: Select type, Step 2: Details

  // GPS location state
  const [gpsLocation, setGpsLocation] = useState(null)
  const [gpsLoading, setGpsLoading] = useState(false)
  const [gpsError, setGpsError] = useState('')

  // Auto-fill current date and time when modal opens
  useEffect(() => {
    if (isOpen && gym) {
      const now = new Date()
      const dateTimeString = now.toISOString().slice(0, 16)
      setVisitDate(dateTimeString)

      const existingVisits = gym.visitLogs?.length || 0
      setCurrentAttempt(existingVisits + 1)

      // Get GPS location automatically
      getGpsLocation()
    }
  }, [isOpen, gym])

  const getGpsLocation = () => {
    if (!navigator.geolocation) {
      setGpsError('Geolocation not supported')
      return
    }

    setGpsLoading(true)
    setGpsError('')

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        })
        setGpsLoading(false)
      },
      (error) => {
        let errorMessage = 'Location error'
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permission denied'
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Unavailable'
            break
          case error.TIMEOUT:
            errorMessage = 'Timed out'
            break
        }
        setGpsError(errorMessage)
        setGpsLoading(false)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  }

  const handleContinue = () => {
    if (!visitType) {
      toast.error('Please select a visit result')
      return
    }
    setStep(2)
  }

  const handleBack = () => {
    setStep(1)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!visitType) {
      toast.error('Please select a visit type')
      return
    }

    if (!notes.trim() || notes.trim().length < 10) {
      toast.error('Visit notes required (min 10 characters)')
      return
    }

    setLoading(true)

    try {
      const response = await axios.post(`/api/gyms/${gym.id}/log-visit`, {
        visitType,
        notes: notes.trim(),
        visitDate: visitDate || undefined,
        gpsLocation: gpsLocation || undefined
      })

      toast.success(response.data.message)
      onSuccess(response.data)
      handleClose()
    } catch (error) {
      console.error('Error logging visit:', error)
      toast.error(error.response?.data?.error || 'Failed to log visit')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setVisitType('')
    setNotes('')
    setVisitDate('')
    setCurrentAttempt(1)
    setStep(1)
    setGpsLocation(null)
    setGpsError('')
    onClose()
  }

  if (!isOpen || !gym) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full max-h-[90vh] overflow-y-auto">
        {/* Header - Compact */}
        <div className="px-4 py-3 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">
              Log Visit #{currentAttempt}
            </h3>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-xs text-gray-500 truncate">{gym.name}</p>

          {/* Warning for 4th visit */}
          {currentAttempt > 3 && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              ⚠️ 4th visit - Gym will be auto-deleted
            </div>
          )}
        </div>

        {/* Step 1: Select Visit Type */}
        {step === 1 && (
          <div className="p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Visit Result *
            </label>

            <div className="space-y-2">
              <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                visitType === 'owner_met'
                  ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                  : 'border-gray-200 hover:bg-green-50'
              }`}>
                <input
                  type="radio"
                  name="visitType"
                  value="owner_met"
                  checked={visitType === 'owner_met'}
                  onChange={(e) => setVisitType(e.target.value)}
                  className="sr-only"
                />
                <span className="text-lg mr-2">✅</span>
                <div className="flex-1">
                  <span className="text-sm font-medium text-green-700">Owner Met</span>
                  <p className="text-xs text-gray-500">Successfully met the owner</p>
                </div>
                {visitType === 'owner_met' && (
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </label>

              <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                visitType === 'owner_not_met'
                  ? 'border-red-500 bg-red-50 ring-2 ring-red-200'
                  : 'border-gray-200 hover:bg-red-50'
              }`}>
                <input
                  type="radio"
                  name="visitType"
                  value="owner_not_met"
                  checked={visitType === 'owner_not_met'}
                  onChange={(e) => setVisitType(e.target.value)}
                  className="sr-only"
                />
                <span className="text-lg mr-2">❌</span>
                <div className="flex-1">
                  <span className="text-sm font-medium text-red-700">Owner Not Met</span>
                  <p className="text-xs text-gray-500">Owner was not available</p>
                </div>
                {visitType === 'owner_not_met' && (
                  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </label>
            </div>

            {/* GPS Status - Compact */}
            <div className="mt-4 flex items-center text-xs">
              {gpsLoading ? (
                <span className="text-gray-500">📍 Getting location...</span>
              ) : gpsLocation ? (
                <span className="text-green-600">📍 Location captured</span>
              ) : gpsError ? (
                <button onClick={getGpsLocation} className="text-red-500 hover:underline">
                  📍 {gpsError} - Tap to retry
                </button>
              ) : null}
            </div>

            {/* Continue Button */}
            <button
              type="button"
              onClick={handleContinue}
              disabled={!visitType}
              className={`w-full mt-4 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center ${
                visitType
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Continue with Details
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Step 2: Details Form */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="p-4">
            {/* Selected Type Summary */}
            <div className={`p-2 rounded-lg mb-4 flex items-center justify-between ${
              visitType === 'owner_met' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              <span className="text-sm font-medium">
                {visitType === 'owner_met' ? '✅ Owner Met' : '❌ Owner Not Met'}
              </span>
              <button type="button" onClick={handleBack} className="text-xs underline">
                Change
              </button>
            </div>

            {/* Date/Time - Compact */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Date & Time
              </label>
              <input
                type="datetime-local"
                value={visitDate}
                onChange={(e) => setVisitDate(e.target.value)}
                className="input w-full text-sm py-2"
                required
              />
            </div>

            {/* GPS Location - Compact display */}
            {gpsLocation && (
              <div className="mb-3 p-2 bg-gray-50 rounded text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-green-600 font-medium">📍 Location</span>
                  <a
                    href={`https://www.google.com/maps?q=${gpsLocation.latitude},${gpsLocation.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    View Map
                  </a>
                </div>
                <div className="text-gray-500 mt-1">
                  {gpsLocation.latitude.toFixed(4)}, {gpsLocation.longitude.toFixed(4)}
                </div>
              </div>
            )}

            {/* Notes - Required */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Visit Notes * <span className="text-gray-400">(min 10 chars)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="input w-full text-sm"
                placeholder="Add visit details..."
                required
              />
              <p className={`text-xs mt-1 ${notes.trim().length >= 10 ? 'text-green-600' : 'text-gray-400'}`}>
                {notes.trim().length}/10
              </p>
            </div>

            {/* Warning for high visit count */}
            {currentAttempt >= 2 && currentAttempt <= 3 && visitType === 'owner_not_met' && (
              <div className="mb-4 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700">
                ⚠️ Visit #{currentAttempt}/3 - Next "Owner Not Met" may remove this gym
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={handleBack}
                className="flex-1 py-2 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Back
              </button>
              <button
                type="submit"
                className="flex-1 py-2 px-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={loading || notes.trim().length < 10}
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  'Log Visit'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default LogVisitModal
